import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Bar } from '@visx/shape';
import { Grid, Box } from '@mui/material';
import { useTooltip, TooltipWithBounds } from '@visx/tooltip';
import { scaleLinear } from '@visx/scale';

import {
  getParams,
  getMonth,
  thousandFormat2,
} from '../../../../Utils/Functions';

let tooltipTimeout;

const PROBABILITY_PALETTES = [
  { active: '#dbe6f2', inactive: '#eef3f8' },
  { active: '#a9bfd5', inactive: '#dde7f0' },
  { active: '#6f93b8', inactive: '#c7d5e4' },
  { active: '#466a91', inactive: '#afc0d1' },
];

function buildTicks(maxValue) {
  if (!maxValue || maxValue <= 0) return [0, 1000];
  const raw = maxValue / 4;
  const magnitude = 10 ** Math.floor(Math.log10(raw || 1));
  const normalized = raw / magnitude;
  let step = magnitude;
  if (normalized > 5) step = 10 * magnitude;
  else if (normalized > 2) step = 5 * magnitude;
  else if (normalized > 1) step = 2 * magnitude;
  const ceiling = Math.ceil(maxValue / step) * step;
  const ticks = [];
  for (let i = 0; i <= 4; i += 1) ticks.push((ceiling / 4) * i);
  return ticks;
}

function getCurrencyUnit(maxValue) {
  return maxValue >= 1000000 ? 'M' : 'K';
}

function formatCurrencyTick(value, unit) {
  if (unit === 'M') return `$${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`;
  return `$${Math.round(value / 1000)}K`;
}


const TopRightChart = ({
  summaryData,
  selectedYears,
  selectedTopRightItems,
  selectedTopLeftItems,
  handleFilter,
}) => {
  const { tooltipOpen, tooltipTop, tooltipLeft, tooltipData, showTooltip, hideTooltip } = useTooltip();

  const [resize, setResize] = useState(false);
  const [hoverBar, setHoverBar] = useState(null);
  const [selectedBars, setSelectedBars] = useState([]);
  const [data, setData] = useState([]);
  const [totals, setTotals] = useState([]);

  const onResize = useCallback(() => {
    setResize(prev => !prev);
  }, []);

  useEffect(() => {
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [onResize]);

  const getItemMonth = useCallback((item) => {
    if (item?.Date) return getMonth(item.Date);
    if (item?.DimDateKey) {
      const key = parseInt(item.DimDateKey, 10);
      if (!Number.isNaN(key)) return Math.floor((key % 10000) / 100) - 1;
    }
    return null;
  }, []);

  const prepareData = useCallback(() => {
    const dictData = {
      0: { percent: 0, PotentialRevenue: 0 },
      1: { percent: 1, PotentialRevenue: 0 },
      2: { percent: 2, PotentialRevenue: 0 },
      3: { percent: 3, PotentialRevenue: 0 },
    };
    const dictTotal = { 0: 0, 1: 0, 2: 0, 3: 0 };
    const filterMonths = [];

    selectedTopLeftItems.forEach((d) => {
      filterMonths.push(d.month);
    });

    if (summaryData && summaryData['Right']) {
      summaryData['Right'].forEach((item) => {
        const month = getItemMonth(item);
        const rawProbability = parseFloat(item.ProbabilityOfSuccess);
        const normalizedProbability =
          Number.isNaN(rawProbability) ? 0 : rawProbability > 1 ? rawProbability / 100 : rawProbability;

        const key =
          normalizedProbability > 0.75
            ? 3
            : normalizedProbability > 0.50
            ? 2
            : normalizedProbability > 0.25
            ? 1
            : 0;

        if (filterMonths.indexOf(month) > -1 || filterMonths.length === 0) {
          const value = parseFloat(item.PotentialRevenue) || 0;
          dictData[key].PotentialRevenue += value;
          dictTotal[key] += value;
        }
      });
    }

    const newData = Object.keys(dictData).map((d) => dictData[d]);
    const newTotals = Object.keys(dictTotal).map((d) => dictTotal[d]);
    newData.sort((a, b) => -a.percent + b.percent);

    setData(newData);
    setTotals(newTotals);
  }, [summaryData, selectedYears, selectedTopLeftItems, getItemMonth]);

  useEffect(() => {
    prepareData();
  }, [prepareData]);

  const getColor = useCallback((item) => {
    const key = item.percent;
    const palette = PROBABILITY_PALETTES[key] || PROBABILITY_PALETTES[0];

    if (selectedTopRightItems.length === 0) return palette.active;

    for (let i = 0; i < selectedTopRightItems.length; i++) {
      if (selectedTopRightItems[i].percent === key) {
        return palette.active;
      }
    }

    return palette.inactive;
  }, [selectedTopRightItems]);

  const handleBar = useCallback((event, item) => {
    let _selectedBars, _selectedTopRightItems;
    let percent = item.percent;
    if (event.shiftKey) {
      _selectedBars = selectedBars.slice();
      _selectedTopRightItems = selectedTopRightItems.slice();

      let index = NaN;
      for (let i = 0; i < selectedBars.length; i++) {
        if (selectedBars[i] === event.target) {
          index = i;
          break;
        }
      }

      if (isNaN(index)) {
        event.target.classList.add('barActive');
        _selectedBars.push(event.target);
        _selectedTopRightItems.push({ percent: percent });
      } else {
        event.target.classList.remove('barActive');
        _selectedBars.splice(index, 1);
        _selectedTopRightItems.splice(index, 1);
      }
    } else {
      let exist = false;

      selectedBars.forEach((selectedBar) => {
        selectedBar.classList.remove('barActive');
        if (selectedBar === event.target) exist = true;
      });

      if (exist && selectedBars.length === 1) {
        _selectedTopRightItems = [];
        _selectedBars = [];
      } else {
        _selectedTopRightItems = [{ percent: percent }];
        _selectedBars = [event.target];
        event.target.classList.add('barActive');
      }
    }

    setSelectedBars(_selectedBars);
    handleFilter({
      selectedTopRightItems: _selectedTopRightItems,
    });
  }, [selectedBars, selectedTopRightItems, handleFilter]);

  const deSelectAll = useCallback(() => {
    if (hoverBar) hoverBar.classList.remove('barHover');

    selectedBars.forEach((selectedBar) => {
      selectedBar.classList.remove('barActive');
    });

    setHoverBar(null);
    setSelectedBars([]);
    localStorage.removeItem('oppSelectedTopRightItems');
    handleFilter({
      selectedTopRightItems: [],
    });
  }, [hoverBar, selectedBars, handleFilter]);

  const showTooltipHandler = useCallback((event, item, isBar = true) => {
    if (tooltipTimeout) clearTimeout(tooltipTimeout);

    let tooltipData = {},
      top,
      left;

    top = event.nativeEvent.clientY - 120;
    left = event.nativeEvent.clientX;

    if (isBar) {
      if (hoverBar) hoverBar.classList.remove('barHover');
      event.target.classList.add('barHover');
      setHoverBar(event.target);

      tooltipData = item;
    } else {
      tooltipData['forecast'] = item;
    }

    tooltipData['isBar'] = isBar;

    showTooltip({
      tooltipData,
      tooltipTop: top,
      tooltipLeft: left,
    });
  }, [hoverBar, showTooltip]);

  const hideTooltipHandler = useCallback(() => {
    tooltipTimeout = setTimeout(() => {
      if (hoverBar) hoverBar.classList.remove('barHover');
      setHoverBar(null);
      hideTooltip();
    }, 300);
  }, [hoverBar, hideTooltip]);

  const width = ((window.innerWidth - 16) * 5) / 12 - 67;
  const height = 200;
  const margin = {
    top: 10,
    right: 24,
    bottom: 20,
    left: 0,
  };

  const { pageYOffset, xMax } = getParams(window, width, height, margin);
  const maxTotal = Math.max(...totals, 0);
  const ticks = buildTicks(maxTotal);
  const axisMax = ticks[ticks.length - 1] || 1000;
  const totalVisibleRevenue = totals.reduce((sum, value) => sum + (Number(value) || 0), 0);
  const currencyUnit = getCurrencyUnit(totalVisibleRevenue);

  const xScale = scaleLinear({
    domain: [0, axisMax || 1],
    range: [0, (xMax * 10) / 12],
    nice: false,
  });

  let chartMap = ['0-25%', '26-50%', '51-75%', '76-100%'];

  return (
    <>
      <strong style={{ fontFamily: 'Verdana !important', fontSize: '12px' }}>
        Potential Fees by Probability
      </strong>

      <Box pt={1}>
        {data.map((item, index) => {
          let topBorder = index === 0 ? 1 : 0;
          let bottomBorder = index === data.length - 1 ? 1 : 0;
          return (
            <Box display="flex" alignItems="center" key={`chart-${index}`}>
              <Box sx={{ minWidth: '67px', px: 0.5 }}>
                <p
                  style={{
                    fontSize: 11,
                    fontWeight: 'bold',
                    whiteSpace: 'nowrap',
                    marginTop: '10px',
                    marginBottom: '10px',
                  }}
                >
                  {chartMap[item.percent]}
                </p>
              </Box>
              <Box
                style={{
                  borderTop: `solid ${topBorder}px #a9a9a9`,
                  borderBottom: `solid ${bottomBorder}px #a9a9a9`,
                  borderLeft: 'solid 1px #a9a9a9',
                  borderRight: 'solid 1px #a9a9a9',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <svg height={29} width="100%">
                  <rect
                    x={0}
                    y={5.5}
                    height={18}
                    width="100%"
                    fill="transparent"
                    onClick={deSelectAll}
                  />
                  <Bar
                    x={0}
                    y={5.5}
                    width={xScale(item.PotentialRevenue)}
                    height={18}
                    fill={getColor(item)}
                    onClick={(event) => handleBar(event, item)}
                    onMouseLeave={hideTooltipHandler}
                    onMouseMove={(event) => showTooltipHandler(event, item)}
                    onTouchEnd={hideTooltipHandler}
                    onTouchMove={(event) => showTooltipHandler(event, item)}
                  />
                </svg>
              </Box>
            </Box>
          );
        })}

        {data.length !== 0 && (
          <Box sx={{ paddingLeft: '67px', paddingRight: '24px' }}>
            <div style={{ position: 'relative', height: 24, width: '100%', overflow: 'visible' }}>
              {ticks.map((tick, idx) => {
                const left = axisMax > 0 ? (tick / axisMax) * ((xMax * 10) / 12) : 0;
                const transform = idx === 0 ? 'translateX(0)' : idx === ticks.length - 1 ? 'translateX(-100%)' : 'translateX(-50%)';
                const textAlign = idx === 0 ? 'left' : idx === ticks.length - 1 ? 'right' : 'center';
                return (
                  <div
                    key={`tick-${tick}-${idx}`}
                    style={{
                      position: 'absolute',
                      left,
                      top: 0,
                      transform,
                      fontSize: 11,
                      textAlign,
                      whiteSpace: 'nowrap',
                      pointerEvents: 'none',
                    }}
                  >
                    <div style={{ borderLeft: '1px solid black', height: 5, margin: idx === 0 ? '0' : idx === ticks.length - 1 ? '0 0 0 auto' : '0 auto' }} />
                    <div style={{ paddingTop: 1 }}>{formatCurrencyTick(tick, currencyUnit)}</div>
                  </div>
                );
              })}
            </div>
          </Box>
        )}
      </Box>

      {tooltipOpen && (
        <TooltipWithBounds
         
          top={tooltipTop + pageYOffset}
          left={tooltipLeft}
        >
          {tooltipData.isBar ? (
            <div className="bkg-white color-black">
              <div className="ft-12">
                <strong>
                  Fee potential:{' '}
                  {thousandFormat2(tooltipData.PotentialRevenue)}
                </strong>
              </div>
            </div>
          ) : null}
        </TooltipWithBounds>
      )}
    </>
  );
};

TopRightChart.propTypes = {
  summaryData: PropTypes.object,
  selectedYears: PropTypes.array,
  selectedTopRightItems: PropTypes.array,
  selectedTopLeftItems: PropTypes.array,
  handleFilter: PropTypes.func,
};

export default TopRightChart;
